import { inject, Injectable } from '@angular/core';
import { ObjectState, StatefullValue } from '@meddev/fe-shared';
import { Store } from '@ngxs/store';
import { SelfBookingState } from '@shared/booking-feature';
import { ErrorCodes, MCAAuthState, MfDocumentDto, UserInfoModel } from '@shared/data';

import _ from 'lodash';
import { catchError, concat, map, Observable, of, switchMap, withLatestFrom } from 'rxjs';
import { CustomerReimbursementClaimModel, CustomerReportClaimModel } from '../../../core/interface/claims/customer-claims-model';
import { BaseReimbursementRequestBodyModel, ReimbursementRequestBodyModel } from '../../../core/interface/claims/reimbursement-body-model';
import { ReportClaimBodyModel } from '../../../core/interface/claims/report-claim-body-model';
import { CofusMedicalDocumentationBodyModel } from '../../../core/interface/documentation/medical-documentation-body-model';
import { MedicalDocumentationModel } from '../../../core/interface/documentation/medical-documentation.model';
import { CustomerReportClaimMapper } from '../../infrastructure/model/mappers/claims/customer-report-claim-mapper';
import { ReimbursementRequestMapper } from '../../infrastructure/model/mappers/claims/reimbursement-claim-body-mapper';
import { ReimbursementClaimMapper } from '../../infrastructure/model/mappers/claims/reimbursement-claim-mapper';
import { ReportClaimMapper } from '../../infrastructure/model/mappers/claims/report-claim-mapper';
import { DocumentationServiceClientService } from '../../infrastructure/service-client/documentation-service-client.service';
import { ReimbursementServiceClientService } from '../../infrastructure/service-client/reimbursement-service-client.service';
import { ReportClaimServiceClientService } from '../../infrastructure/service-client/report-claim-service-client.service';
import { ClaimSubmissionHelper } from '../model/claim-submission-helper';

@Injectable({
    providedIn: 'root',
})
export class ClaimManagerService {
    private store = inject(Store);
    private documentationServiceClientService = inject(DocumentationServiceClientService);
    private reportClaimService = inject(ReportClaimServiceClientService);
    private reimbursementService = inject(ReimbursementServiceClientService);

    public setMedicalDocumentation(request: CofusMedicalDocumentationBodyModel, files?: File[]): Observable<MedicalDocumentationModel> {
        return this.store.selectOnce(MCAAuthState.userInfo$).pipe(
            switchMap(userInfo => {
                const documentationServiceHelper = new ClaimSubmissionHelper(this.documentationServiceClientService);
                return documentationServiceHelper.processDocumentationRequest(userInfo as UserInfoModel, request, files);
            }),
        );
    }

    public sendReportClaim(request: ReportClaimBodyModel): Observable<ReportClaimBodyModel> {
        return of(null).pipe(
            switchMap(() => {
                const requestDto = new ReportClaimMapper().mapReportClaimBodyModelToDto(request);
                return this.reportClaimService.sendReportClaim(requestDto).pipe(
                    map(result => {
                        const model = new ReportClaimMapper().mapReportClaimBodyDtoToModel(result);
                        return model;
                    }),
                );
            }),
        );
    }

    public sendReimbursementClaim(request: BaseReimbursementRequestBodyModel, policyNumber: string, files?: File[]): Observable<string> {
        return this.store.selectOnce(MCAAuthState.userInfo$).pipe(
            switchMap(userInfo => {
                const documentationServiceHelper = new ClaimSubmissionHelper(this.documentationServiceClientService);
                return documentationServiceHelper.handleFileUploads(files).pipe(
                    switchMap(documentIds => {
                        const modifiedRequest: ReimbursementRequestBodyModel = { customerId: userInfo?.id as string, ...request };
                        modifiedRequest.insurancePolicyNumber = policyNumber;
                        if (documentIds?.length) {
                            modifiedRequest.maskedDocumentIds = documentIds;
                        }
                        const requestDto = new ReimbursementRequestMapper().mapModelToDto(modifiedRequest);
                        return this.reimbursementService.sendReimbursementClaim(requestDto).pipe();
                    }),
                );
            }),
        );
    }

    public cancelReportClaim(reportClaimId: string): Observable<unknown> {
        return this.store
            .selectOnce(MCAAuthState.userInfo$)
            .pipe(switchMap(userInfo => this.reportClaimService.cancelReportClaim(userInfo?.id as string, reportClaimId)));
    }

    public cancelReimbursementClaim(reimbursementId: string): Observable<unknown> {
        return this.store
            .selectOnce(MCAAuthState.userInfo$)
            .pipe(switchMap(userInfo => this.reimbursementService.cancelReimbursementClaim(userInfo?.id as string, reimbursementId)));
    }

    public getCustomerReportClaims(): Observable<StatefullValue<CustomerReportClaimModel[], ErrorCodes>> {
        return this.store.selectOnce(MCAAuthState.userInfo$).pipe(
            switchMap(userInfo =>
                concat(
                    of({ value: null as unknown as CustomerReportClaimModel[], state: ObjectState.LOADING }),
                    of(null).pipe(
                        switchMap(() => {
                            return this.reportClaimService.getCustomerReportClaims(userInfo?.id as string).pipe(
                                withLatestFrom(this.store.selectOnce(SelfBookingState.lastBookedAllowanceToken$)),
                                map(([results, lastBookedAllowanceToken]) => {
                                    const model = results.map(result => {
                                        return new CustomerReportClaimMapper().mapDtoToModel(result);
                                    });

                                    const matchedClaim = model.find(
                                        claim => !_.isNil(claim.allowanceToken) && claim.allowanceToken === lastBookedAllowanceToken,
                                    );
                                    if (matchedClaim) {
                                        matchedClaim.isUsed = true;
                                    }

                                    const returnObj = { value: model, state: ObjectState.VALID };
                                    return returnObj;
                                }),
                                catchError(() => {
                                    return of({ value: null as unknown as CustomerReportClaimModel[], state: ObjectState.ERROR });
                                }),
                            );
                        }),
                    ),
                ),
            ),
        );
    }

    public getCustomerReimbursementtClaims(): Observable<StatefullValue<CustomerReimbursementClaimModel[], ErrorCodes>> {
        return this.store.selectOnce(MCAAuthState.userInfo$).pipe(
            switchMap(userInfo =>
                concat(
                    of({ value: null as unknown as CustomerReimbursementClaimModel[], state: ObjectState.LOADING }),
                    of(null).pipe(
                        switchMap(() => {
                            return this.reimbursementService.getCustomerReimbursementClaims(userInfo?.id as string).pipe(
                                map(results => {
                                    const model = results.map(result => {
                                        return new ReimbursementClaimMapper().mapDtoToModel(result);
                                    });
                                    const returnObj = { value: model, state: ObjectState.VALID };
                                    return returnObj;
                                }),
                                catchError(() => {
                                    return of({ value: null as unknown as CustomerReimbursementClaimModel[], state: ObjectState.ERROR });
                                }),
                            );
                        }),
                    ),
                ),
            ),
        );
    }

    public getReportClaimDocument(reportClaimId: string, documentId: string, medifitDocumentId?: string): Observable<MfDocumentDto> {
        return this.getDocument(reportClaimId, documentId, medifitDocumentId, this.reportClaimService);
    }

    public getReimbursementDocument(reimbursementId: string, documentId: string, medifitDocumentId?: string): Observable<MfDocumentDto> {
        return this.getDocument(reimbursementId, documentId, medifitDocumentId, this.reimbursementService);
    }

    private getDocument(
        id: string,
        documentId: string,
        medifitDocumentId: string | undefined,
        service: {
            getMedifitDocument: (id: string, userId: string, medifitDocumentId: string) => Observable<MfDocumentDto>;
            getExternalDocument: (id: string, userId: string, documentId: string) => Observable<MfDocumentDto>;
        },
    ): Observable<MfDocumentDto> {
        return this.store.selectOnce(MCAAuthState.userInfo$).pipe(
            switchMap(userInfo => {
                if (documentId && medifitDocumentId) {
                    return service.getMedifitDocument(id, userInfo?.id as string, medifitDocumentId);
                } else if (documentId && !medifitDocumentId) {
                    return service.getExternalDocument(id, userInfo?.id as string, documentId);
                } else {
                    throw new Error('Invalid document parameters');
                }
            }),
            map((documentDto: MfDocumentDto) => ({
                mediaType: documentDto.mediaType,
                data: documentDto.data,
            })),
        );
    }
}
